import { Grid } from '@mui/material';
import { RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { motion } from 'framer-motion';
import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import NorthStarIcon from '../../../../assets/svg/NorthStarIcon';
import { DRIVERS_LAYOUT_ID } from '../../../../components/DriversLayout';
import { LoadingButton, LoadingButtonProps } from '../../../../components/LoadingButton';
import { Text } from '../../../../react-components/Text';

export interface RouteNotificationProps {
  open: boolean;
  id?: string;
  onClose?: (reason: `timeout` | `discard`) => void;
  onClick?: () => void;
  header?: {
    text?: string;
    intlId?: string;
    intlValues?: Record<string, string | number>;
    containerStyle?: React.CSSProperties;
  };
  subtitle?: {
    text?: string;
    intlId?: string;
    intlValues?: Record<string, string | number>;
    containerStyle?: React.CSSProperties;
  };
  isLoading?: boolean;
  buttons?: Array<LoadingButtonProps>;
  portalElementId?: string;
  time?: number;
  expandableContent?: React.ReactNode;
}
const NOTIFICATION_TIME = 15; // seconds
const generateRandomId = () => `mobile-modal-${Math.random()}`;

export const RouteNotification = ({
  open,
  id,
  onClose,
  onClick,
  header,
  subtitle,
  isLoading,
  buttons,
  portalElementId = DRIVERS_LAYOUT_ID,
  children,
  time = NOTIFICATION_TIME,
  expandableContent,
}: PropsWithChildren<RouteNotificationProps>) => {
  const [openContent, setOpenContent] = useState(false);

  const keyRef = useRef<string>(id ?? generateRandomId());

  useEffect(() => {
    if (keyRef.current === null && keyRef.current !== id) {
      keyRef.current = id ?? generateRandomId();
    }
  }, [id]);

  if (!open) return null;

  const content = (
    <motion.div
      key={keyRef.current}
      drag={!openContent ? `x` : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.3}
      onDragEnd={(event, info) => {
        if (Math.abs(info.offset.x) > 100) {
          setOpenContent(false);
          onClose?.(`discard`); // close when dragged more than 100px horizontally
        }
      }}
      onClick={() => {
        onClick?.();
        expandableContent && setOpenContent(true);
      }}
      style={{
        backgroundColor: `${RoutalPalette.white}FF`,
        width: `calc(100% - 20px)`,
        height: `fit-content`,
        borderRadius: `10px`,
        display: `flex`,
        flexWrap: `wrap`,
        alignItems: `center`,
        flexDirection: `column`,
        padding: `10px 0px 0px 0px`,
        border: `1px solid ${RoutalPalette.secondary00}`,
        zIndex: 1009,
        position: `absolute`,
        top: `10px`,
        left: `10px`,
      }}
      className="animate__animated animate__fadeIn animate__faster"
    >
      <Grid
        style={{
          display: `flex`,
          width: `100%`,
          alignItems: `center`,
          flexDirection: `column`,
          padding: `00px 10px 0px 10px`,
        }}
      >
        <Grid style={{ display: `flex`, width: `100%`, alignItems: `center`, flexDirection: `row` }}>
          <NorthStarIcon height="13px" width="13px" color={RoutalPalette.secondary20} style={{ paddingRight: `8px` }} />
          {header && (
            <>
              {header.intlId ? (
                <Text
                  variant="h6"
                  style={{
                    textAlign: `center`,
                    color: RoutalPalette.primary40,
                    fontWeight: `bold`,
                    fontSize: `13px`,
                  }}
                  intlId={header.intlId}
                  intlValues={header.intlValues}
                />
              ) : (
                <Text
                  variant="h6"
                  style={{
                    textAlign: `center`,
                    color: RoutalPalette.primary40,
                    fontWeight: `bold`,
                    fontSize: `13px`,
                  }}
                >
                  {header.text}
                </Text>
              )}
            </>
          )}
        </Grid>
        <VSpacer small />

        {subtitle && (
          <Grid style={{ marginBottom: `8px`, ...subtitle.containerStyle }}>
            {subtitle.intlId ? (
              <Text
                variant="body"
                style={{ textAlign: `center` }}
                intlId={subtitle.intlId}
                intlValues={subtitle.intlValues}
              />
            ) : (
              <Text variant="body" style={{ textAlign: `center` }}>
                {subtitle.text}
              </Text>
            )}
          </Grid>
        )}

        {children && (
          <>
            <VSpacer small />
            {children}
          </>
        )}
        <VSpacer small />

        {buttons && (
          <Grid container direction="column" style={{ width: `100%` }}>
            {buttons.map(({ style, ...buttonProps }, index) => (
              <LoadingButton key={index} style={{ ...style, height: `42px` }} {...buttonProps} />
            ))}
          </Grid>
        )}
        {expandableContent && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={openContent ? { height: `auto`, opacity: 1 } : { height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: `easeInOut` }}
            style={{
              overflow: `hidden`,
              width: `100%`,
              padding: openContent ? `10px` : `0px`,
            }}
          >
            {expandableContent}
          </motion.div>
        )}
      </Grid>
      {!openContent && (
        <Grid
          style={{
            width: `99%`,
            height: `4px`,
            backgroundColor: RoutalPalette.secondary20n,
            position: `relative`,
            overflow: `hidden`,
            borderBottomLeftRadius: `10px`, // Match parent's border-radius
            borderBottomRightRadius: `10px`, // Match parent's border-radius
          }}
        >
          <div
            style={{
              width: `100%`,
              height: `100%`,
              backgroundColor: RoutalPalette.primary40,
              position: `absolute`,
              animation: `progressBar ${time}s linear forwards`,
            }}
            onAnimationEnd={() => {
              setOpenContent(false);
              onClose?.(`timeout`);
            }}
          />
          <style>
            {`
                @keyframes progressBar {
                  from {
                    transform: translateX(0);
                  }
                  to {
                    transform: translateX(-100%);
                  }
                }
              `}
          </style>
        </Grid>
      )}
    </motion.div>
  );

  const element = document.getElementById(portalElementId ?? ``);
  if (element) {
    return createPortal(content, element as HTMLElement);
  }

  return content;
};
